/**
 * Table row for an establishment on the gamification leaderboard.
 */
import * as React from 'react';
import { Building2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LeaderboardRankBadge } from '@/components/gamification/LeaderboardRankBadge';
import {
  establishmentIconBoxClass,
  establishmentIconClass,
  establishmentMetaLabel,
  leaderboardRowClassName,
} from '@/components/gamification/gamification-utils';
import type { EstablishmentLeaderboardEntry } from '@/types/gamification.types';

/** Props for {@link EstablishmentLeaderboardRow}. */
export interface EstablishmentLeaderboardRowProps {
  entry: EstablishmentLeaderboardEntry;
  isHighlighted?: boolean;
  highlightLabel?: string;
  showRank?: boolean;
  compact?: boolean;
  className?: string;
}

/** Establishment leaderboard row: rank, name, city, tutor count, and XP. */
export const EstablishmentLeaderboardRow: React.FC<EstablishmentLeaderboardRowProps> = ({
  entry,
  isHighlighted = false,
  highlightLabel = 'Votre établissement',
  showRank = true,
  compact = false,
  className,
}) => {
  const showTutorCount = entry.tutorCount > 0 && !compact;

  return (
    <div className={cn(leaderboardRowClassName(compact, isHighlighted), className)}>
      {showRank ? <LeaderboardRankBadge rank={entry.rank} /> : null}
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-lg bg-muted text-[var(--color-1-nuance-1)]',
          establishmentIconBoxClass(compact),
        )}
        aria-hidden
      >
        <Building2 className={establishmentIconClass(compact)} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-foreground">
          {entry.displayName}
          {isHighlighted ? (
            <span className="ml-1.5 text-xs font-medium text-[var(--color-2-nuance-1)]">
              ({highlightLabel})
            </span>
          ) : null}
        </p>
        <p className="truncate text-sm text-muted-foreground">
          {establishmentMetaLabel(entry.city, entry.tutorCount)}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="text-sm font-bold text-[var(--color-1-nuance-1)]">{entry.totalXp} XP</span>
        {showTutorCount ? (
          <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
            <Users className="h-3 w-3 text-[var(--color-1-nuance-1)]" aria-hidden />
            {entry.tutorCount}
          </span>
        ) : null}
      </div>
    </div>
  );
};
